import { useState, useContext } from "react";
import { AuthContext } from "@/features/auth/Context/AuthContext";
import { toast } from "sonner";
import { useLocation } from "wouter";
import { motion } from 'framer-motion';
import { validatePassword } from "@/components/utils/validators";
import { useChangerPasswordMutation } from "@/features/users/usersApi";
import { Button } from "@/components/ui/button";
import { containerVariants } from "@/components/animations/animations-motion";

export default function ForcePasswordResetPage() {
  const auth = useContext(AuthContext);
  if (!auth) throw new Error("AuthContext not found. Did you forget to wrap your app with <AuthProvider />?");
  const [, navigate] = useLocation();
  const { user, logout, setIsDefault } = auth;
  const [ changePassword , { isLoading: isUpdating } ] = useChangerPasswordMutation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const password = e.target.value;
    setNewPassword(password);
    
    // Validar contraseña
    const validation = validatePassword(password);
    setPasswordErrors(validation.errors);
    
    // Validar confirmación si ya existe
    if (confirmPassword && password !== confirmPassword) {
      setConfirmError("Las contraseñas no coinciden");
    } else {
      setConfirmError(null);
    }
  };

  const handleConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const confirmValue = e.target.value;
    setConfirmPassword(confirmValue);
    
    // Validar que coincidan
    if (newPassword !== confirmValue) {
      setConfirmError("Las contraseñas no coinciden");
    } else {
      setConfirmError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validar contraseña
    const validation = validatePassword(newPassword);
    setPasswordErrors(validation.errors);
    
    // Verificar que las contraseñas coincidan
    if (newPassword !== confirmPassword) {
      setConfirmError("Las contraseñas no coinciden");
      return;
    }
    
    // Si todo es válido, cambiar contraseña
    if (validation.isValid && !confirmError) {
      try {
        const result = await changePassword({
          id: user!.id,
          password: newPassword
        }).unwrap();

        toast.success(result.message);
        setIsDefault(false);
        navigate("/");
      } catch (err) {
        toast.error("Error actualizando contraseña");
        console.error(err);
      } 
    }
  };


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] p-4">
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="bg-white shadow-2xl rounded-xl p-8 w-full max-w-md border border-gray-200"
        >
        {/* Encabezado con información del usuario */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-[var(--color-accent)] rounded-full flex items-center justify-center text-white font-bold text-xl mx-auto mb-4">
            {user?.name?.charAt(0)}{user?.lastname?.charAt(0)}
          </div>
          <h1 className="text-xl font-semibold text-gray-800">
            {user?.name} {user?.lastname}
          </h1>
          <p className="text-gray-600 text-sm">{user?.username}</p>
        </div>

        <h2 className="text-2xl font-bold text-gray-800 mb-2 text-center">
          Cambio de Contraseña Requerido
        </h2>

        <p className="text-gray-600 text-center mb-6">
          Por razones de seguridad, debe cambiar su contraseña temporal antes de continuar.
        </p>

        {/* Alertas informativas */}
        <div className="mb-6 space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start justify-center">
            <div className="text-red-500 mr-2 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-red-700 text-sm">Este paso es obligatorio para continuar</p>
          </div>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start">
            <div className="text-blue-500 mr-2 mt-0.5">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-blue-700 text-sm">
              La contraseña debe tener al menos 8 caracteres, incluir una letra mayúscula, 
              una minúscula, un número y un símbolo especial.
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nueva Contraseña
            </label>
            <input
              type="password"
              className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition ${
                passwordErrors.length > 0 ? "border-red-500" : "border-gray-300"
              }`}
              value={newPassword}
              onChange={handlePasswordChange}
              required
            />
            {passwordErrors.length > 0 && (
              <div className="mt-2 text-sm text-red-500">
                <ul className="list-disc pl-5 space-y-1">
                  {passwordErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar Contraseña
            </label>
            <input
              type="password"
              className={`w-full border rounded-lg p-3 focus:ring-2 focus:ring-[var(--color-accent)] focus:border-transparent transition ${
                confirmError ? "border-red-500" : "border-gray-300"
              }`}
              value={confirmPassword}
              onChange={handleConfirmChange}
              required
            />
            {confirmError && (
              <div className="mt-2 text-sm text-red-500">
                {confirmError}
              </div>
            )}
          </div>

          <Button
            disabled={isUpdating || passwordErrors.length > 0 || !!confirmError || !newPassword || !confirmPassword}
            className="w-full bg-[var(--color-accent)] text-white rounded-lg py-3 font-medium hover:bg-[#2a7aaf]  transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isUpdating ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Guardando...
              </>
            ) : (
              "Guardar Nueva Contraseña"
            )}
          </Button>
        </form>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={logout}
            className="w-full text-center text-gray-600 hover:text-red-600 hover:scale-105 transition flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
            </svg>
            Cerrar sesión
          </button>
        </div>
      </motion.div>
    </div>
  );
}